import { apiClient } from '@indipia/shared/api/client';
import { API_PRX } from '@indipia/shared/api/endpoints';
import { byPhotoSeq } from '../utils/photoOrder';
import type {
  ApiResponse,
  BasicInfo,
  BulkCandidatesResp,
  BulkDatePreset,
  BulkPreviewRequest,
  BulkPreviewResult,
  BulkWorkOrderCreateRequest,
  BulkWorkOrderCreateResult,
  CustomerReportPublic,
  CustomerReportSendRequest,
  CustomerReportSendResult,
  CustomerShareRequest,
  CustomerShareResult,
  DashboardSummary,
  IndiPhotoPage,
  IndiPhotoSendResult,
  FieldReportHistory,
  FieldReportDetail,
  FieldReportListItem,
  FieldReportListQuery,
  FieldReportOwnerCreateRequest,
  FieldReportOwnerCreateResult,
  FieldReportSubmitRequest,
  FieldReportSubmitResult,
  MyKeyword,
  PagedList,
  Partner,
  PartnerCreateRequest,
  ResendResult,
  ShareHistoryQuery,
  ShareHistoryRow,
  ShareRevokeImpact,
  ShareRevokeResult,
  CustomerReportPublishResult,
  WorkOrderCreateRequest,
  WorkOrderCreateResult,
  WorkOrderListItem,
  WorkOrderListQuery,
  WorkOrderNotice,
  WorkOrderSiteInfo,
  WorklogCreateRequest,
  WorklogCreateResult,
  WorklogDeleteInfo,
  WorklogListItem,
  WorklogListQuery,
  WorklogTimelineItem,
  WorklogTimelineQuery,
  ScheduleLookupResult,
} from '../types/worklog.types';

/* ─────────────────────────────────────────────────────────────────────────────
 * ▼▼▼ [로컬 테스트 전용 — 깃 반영 전 원복 대상 ①] ▼▼▼
 *
 * 공유(GUEST) 화면은 운영에서 pf-sharegate 발급 `share_jwt` 쿠키로만 주체를 식별한다
 * (F-04 §9 — URL에 shareId 노출 금지). 그래서 공유 API는 `/me` 패턴이고 파라미터가 없다.
 *
 * 그런데 로컬에는 그 쿠키가 없어 404(SHARE_NOT_FOUND)가 난다. sv-worklog가 이 상황을 위해
 * `app.dev.share-scope-from-query: true`(application-local.yml) 우회를 제공하므로,
 * **개발 모드에서만** 브라우저 주소창의 `?shareId=` 를 읽어 쿼리로 전달한다.
 *
 * `import.meta.env.DEV` 분기라 운영 빌드에서는 트리셰이킹으로 제거되어 동작이 동일하다.
 * 그래도 원복을 원하면 이 블록과 각 호출부의 `...devShareParam()` 를 지우면 된다.
 * ───────────────────────────────────────────────────────────────────────────── */
/** 개발 모드에서만 주소창의 shareId 를 쿼리 params 로 만든다. 운영/미존재 시 빈 객체. */
function devShareParam(): { params?: Record<string, string> } {
  if (!import.meta.env.DEV) return {};
  const id = new URLSearchParams(window.location.search).get('shareId');
  return id ? { params: { shareId: id } } : {};
}

/**
 * 개발 모드에서만 게스트 공유 사진 URL(직접 {@code <img src>})에 주소창 shareId 를 붙인다.
 * 운영은 공유쿠키로 스코프가 잡히므로 원본 URL 그대로 반환한다. 빈 URL·shareId 미존재 시 원본 반환.
 */
export function withDevShareId(url: string | null | undefined): string | null | undefined {
  if (!import.meta.env.DEV || !url) return url;
  const id = new URLSearchParams(window.location.search).get('shareId');
  if (!id) return url;
  return url + (url.includes('?') ? '&' : '?') + 'shareId=' + id;
}
/* ▲▲▲ [로컬 테스트 전용 — 원복 대상 ①] ▲▲▲ */

/**
 * 사진 배열 정렬을 api 계층에서 끝낸다 — 조회 응답의 `photos[]` 를 photoSeq 오름차순으로 맞춘다.
 * 화면마다 정렬을 흩뿌리지 않기 위한 단일 지점이며, 화면의 index 기반 상태(선별 등)도
 * 항상 이 정렬된 배열에서 파생되므로 일관성이 유지된다.
 */
function orderPhotos<T extends { photos?: Array<{ photoSeq?: number | null }> | null }>(item: T): T {
  if (item?.photos?.length) item.photos = byPhotoSeq(item.photos);
  return item;
}

/** 인디공사현황(sv-worklog) API — FE 호출 경로 /api/worklog/... (API명세 §0) */
export const worklogApi = {
  /** 메인 대시보드 요약 (API명세 §5.1) */
  getDashboardSummary(signal?: AbortSignal) {
    return apiClient.get<ApiResponse<DashboardSummary>>(
      `${API_PRX.WORKLOG}/dashboard/summary`,
      { signal },
    );
  },

  /**
   * 기준정보 통합 조회 (API명세 §5.3) — 에이전트 진입 시 1회 부트스트랩.
   * subscriptIds: 구독중 에이전트 번호(CSV). 8(인디플래너) 포함 시에만 schedules 채워짐.
   * X-User-Id·X-Comp-Id는 게이트웨이가 주입하므로 FE에서 별도 세팅 불필요.
   */
  /**
   * 공통코드 조회 — sv-indipia `TC_CODE_LIST` (`USE_YN='Y'`만, `codeOrder` 오름차순).
   *
   * `GET /indipia/init`이 적재하는 appStore.codes 는 **플랫폼(sv-zeus) `ZEU_*` 계열만** 담고 있어
   * `E01_SCHEDULE_STATUS`(공사진행상태) 같은 sv-indipia 코드그룹은 여기로 별도 조회해야 한다.
   *
   * ⚠️ 경로·파라미터 주의 — 공사현황 API명세 §9.1의 `GET /api/common/codes?groupCode=` 는 **오기**다.
   *    실제는 C-03 §3.1 service-prefix 규칙(sv-indipia = `/indipia`)에 따라
   *    `GET /api/indipia/codes?codeGroupCode=` 이고, 응답 `data`는 **객체가 아니라 코드 배열**이다.
   *    (플랫폼 공통코드 스펙 §7과 동일 계약 — codeGroupCode/code/codeName/codeOrder)
   */
  getCommonCodes(codeGroupCode: string, signal?: AbortSignal) {
    return apiClient.get<
      ApiResponse<Array<{ codeGroupCode: string; codeValue: string; codeName: string; codeOrder: number }>>
    >(`${API_PRX.INDIPIA}/codes`, { params: { codeGroupCode }, signal });
  },

  getBasicInfo(subscriptIds: number[], signal?: AbortSignal) {
    const params: Record<string, string> = {};
    if (subscriptIds.length) params.subscriptIds = subscriptIds.join(',');
    return apiClient.get<ApiResponse<BasicInfo>>(
      `${API_PRX.WORKLOG}/basic-info`,
      { params, signal },
    );
  },

  /**
   * 반장 작업요청 안내 조회 — 외부·무인증 공유 화면 (API명세 §2.14).
   * 자격증명은 pf-sharegate 발급 공유 쿠키(shareScope). 토큰 미부착(auth-exempt).
   * 경로가 client.ts AUTH_EXEMPT_PATHS에 등재돼 Authorization 헤더가 생략된다.
   */
  getWorkOrderNoticeShare(signal?: AbortSignal) {
    return apiClient
      .get<ApiResponse<WorkOrderNotice>>(
        `${API_PRX.WORKLOG}/work-order-shares/me`,
        { ...devShareParam(), signal },   // [원복 대상 ①] devShareParam() 제거
      )
      .then((res) => {
        if (res.data.data) orderPhotos(res.data.data);
        return res;
      });
  },

  /**
   * 반장 현장보고 제출 — 외부·무인증 공유 화면 (API명세 §3.9). multipart/form-data.
   * 현장(worklogId)·작업요청(workOrderId)을 보내지 않는다 — 서버가 공유 스코프에서 도출한다.
   * 경로가 `/work-order-shares/me` 로 시작해 client.ts AUTH_EXEMPT_PATHS(부분문자열 매칭)에 걸린다.
   *
   * 멱등: 같은 submitUuid 재전송은 에러가 아니라 200 + 최초 fieldReportId 반환(§3.9).
   */
  submitFieldReportShare(body: FieldReportSubmitRequest, signal?: AbortSignal) {
    const form = new FormData();
    form.append('submitUuid', body.submitUuid);
    body.workTypeCodes.forEach((code) => form.append('workTypeCodes', code));
    if (body.workCmplYn) {
      form.append('workCmplYn', body.workCmplYn);
    }
    if (body.workDate) {
      form.append('workDate', body.workDate);
    }
    if (body.issueTypeCodes?.length) {
      body.issueTypeCodes.forEach((code) => form.append('issueTypeCodes', code));
    }
    form.append('reportContent', body.reportContent);
    // 사진 메타는 JSON 문자열 폼필드(BE §3.3 규약). photoFiles 와 photoSeq 순서로 대응.
    if (body.photos?.length) {
      form.append('photos', JSON.stringify(body.photos));
      body.photoFiles?.forEach((f) => form.append('photoFiles', f));
    }
    return apiClient.post<ApiResponse<FieldReportSubmitResult>>(
      `${API_PRX.WORKLOG}/work-order-shares/me/field-reports`,
      form,
      { ...devShareParam(), signal },   // [원복 대상 ①] devShareParam() 제거
    );
  },

  /**
   * 반장 전송이력 조회 — 외부·무인증 공유 화면 (API명세 §3.10).
   * 이 공유링크로 본인이 제출한 보고만 반환. 작성 만료 후에도 조회는 허용된다.
   */
  getFieldReportHistoryShare(page: number, size: number, signal?: AbortSignal) {
    const dev = devShareParam();
    return apiClient
      .get<ApiResponse<FieldReportHistory>>(
        `${API_PRX.WORKLOG}/work-order-shares/me/field-reports`,
        { params: { page, size, ...dev.params }, signal },   // [원복 대상 ①] devShareParam() 병합
      )
      .then((res) => {
        res.data.data?.items?.forEach(orderPhotos);
        return res;
      });
  },

  /**
   * 현장보고 목록 조회 — 사장님(JWT) (API명세 §3.1).
   * 열람(읽음) 처리는 폐기됐다(기획 004 D·설계서 §5.3) — readYn 모드·/read·/unRead 는 호출하지 않는다.
   * 현장(siteId) 필터는 서버 조건이 아니라 응답 items[].siteId로 화면에서 처리한다(§3.1).
   */
  getFieldReports(query: FieldReportListQuery, signal?: AbortSignal) {
    const params: Record<string, string | number> = {};
    if (query.reportDateFrom) params.reportDateFrom = query.reportDateFrom;
    if (query.reportDateTo) params.reportDateTo = query.reportDateTo;
    if (query.worklogId != null) params.worklogId = query.worklogId;
    if (query.partnerId?.length) params.partnerId = query.partnerId.join(',');
    if (query.includeOwner) params.includeOwner = 'true'; // 작성자 필터에 로그인 사장님(OWNER 작성분) 포함
    if (query.issueTypeCode?.length) params.issueTypeCode = query.issueTypeCode.join(',');
    if (query.page != null) params.page = query.page;   // BE offset 페이징(page/size)
    if (query.size != null) params.size = query.size;
    if (query.sort) params.sort = query.sort;
    return apiClient
      .get<ApiResponse<PagedList<FieldReportListItem>>>(`${API_PRX.WORKLOG}/field-reports`, { params, signal })
      .then((res) => {
        res.data.data?.items?.forEach(orderPhotos);
        return res;
      });
  },

  /**
   * 공사현황 통합 타임라인 조회 — 사장님(JWT) (API명세 §1.5).
   * 작업요청+현장보고를 서버가 발생일시 내림차순으로 병합·페이징한다(offset 무한스크롤).
   * 화면은 item.type 으로 유형별 카드를 렌더한다.
   */
  getTimeline(query: WorklogTimelineQuery, signal?: AbortSignal) {
    const params: Record<string, string | number> = {};
    params.dateFrom = query.dateFrom;
    params.dateTo = query.dateTo;
    if (query.type) params.type = query.type;
    if (query.siteId != null) params.siteId = query.siteId;   // 단일 현장 피드(나의 현장 공사현황 탭) — 서버 필터
    if (query.page != null) params.page = query.page;   // BE offset 페이징(page/size)
    if (query.size != null) params.size = query.size;
    return apiClient
      .get<ApiResponse<PagedList<WorklogTimelineItem>>>(`${API_PRX.WORKLOG}/timeline`, { params, signal })
      .then((res) => {
        // 타임라인은 작업요청(§2.1)·현장보고(§3.1) 카드가 섞여 온다 — 채워진 쪽만 정렬.
        res.data.data?.items?.forEach((it) => {
          if (it.workOrder) orderPhotos(it.workOrder);
          if (it.fieldReport) orderPhotos(it.fieldReport);
        });
        return res;
      });
  },

  /**
   * 고객공유 선정(초안) 저장 — 메시지·사진 선택을 저장만 한다(API명세 §4.1.11, 결정 5-2·5-6).
   * 선언적 전체 교체: body가 최종 공유 상태 전체이며 빠진 사진은 해제된다. 발송이 아니다 — 발송은 sendCustomerReport.
   * 발송완료(잠금) 건에 호출하면 FIELD_REPORT_LOCKED로 거부된다.
   */
  setCustomerShare(fieldReportId: number, body: CustomerShareRequest) {
    return apiClient.put<ApiResponse<CustomerShareResult>>(
      `${API_PRX.WORKLOG}/field-reports/${fieldReportId}/customer-share`,
      body,
    );
  },

  /**
   * 인디포토 전송 — 현장보고 사진을 인디포토로 전송 (API명세 §3.8).
   * 추가 전송(POST): 이미 전송됐거나 인디포토 출처인 사진은 서버가 제외한다.
   */
  sendIndiPhotos(fieldReportId: number, frPhotoIds: number[]) {
    return apiClient.post<ApiResponse<IndiPhotoSendResult>>(
      `${API_PRX.WORKLOG}/field-reports/${fieldReportId}/indi-photos`,
      { frPhotoIds },
    );
  },

  /**
   * 인디포토 현장 사진 목록 ※ sv-photo — 작업요청 '인디포토 사진첩' 선택 팝업용.
   *
   * 다른 에이전트(photo-organizer) 코드를 import 하지 않고 이 에이전트가 직접 호출한다(CLAUDE.md §9).
   * API명세 §8.1 은 `GET /photo/indi-photos?siteId&from&to` 로 적혀 있으나,
   * 실제 sv-photo 가 제공하고 인디포토 화면이 쓰는 경로는 아래 현장 스코프 경로다.
   */
  getIndiPhotos(siteId: number, limit = 100, offset = 0, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<IndiPhotoPage>>(
      `${API_PRX.PHOTO}/sites/${siteId}/photos`,
      { params: { limit, offset }, signal },
    );
  },

  /**
   * 고객 공개 리포트 조회 — 외부·무인증 공유 화면 (API명세 §4.1.13). 스냅샷 기준(결정 5-11).
   * 자격증명은 pf-sharegate 발급 공유 쿠키(shareScope). shareId는 URL에 싣지 않는다(F-04 §9).
   * BE 경로는 반장 경계와 동일한 `/customer-report-shares/me` 패턴.
   */
  getCustomerReportShare(signal?: AbortSignal) {
    return apiClient
      .get<ApiResponse<CustomerReportPublic>>(
        `${API_PRX.WORKLOG}/customer-report-shares/me`,
        { ...devShareParam(), signal },   // [원복 대상 ①] devShareParam() 제거
      )
      .then((res) => {
        // 발송 스냅샷 카드 → 사진 순으로 중첩된다.
        res.data.data?.reportCards?.forEach(orderPhotos);
        return res;
      });
  },

  /**
   * 고객 발송 — 사장님(JWT) (API명세 §4.1.12, 결정 5-3~5-6).
   * body.fieldReportIds(화면에 보이는 미발송·공유내용 있는 건만)·requestKey(UUID, 중복방지키)를 보낸다.
   * 발송분은 스냅샷으로 고정·잠긴다(수정·재발송 불가). compName은 서버가 회원정보로 채운다(요청에 없음).
   * 링크는 발급됐으나 알림톡 접수만 실패하면 200 + dispatchId=null 로 내려온다(부분 실패).
   */
  sendCustomerReport(worklogId: number, body: CustomerReportSendRequest, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<CustomerReportSendResult>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}/customer-reports/send`,
      body,
      { signal },
    );
  },

  /**
   * 현장보고 등록(사장님) — 사장님(JWT), multipart (API명세 §3.3·§4.1.8).
   * 사진 메타는 JSON 문자열, 바이너리는 photoFiles[](photoSeq 순, source=FILE분만). 인디포토(INDI_PHOTO) 지원.
   */
  createFieldReport(worklogId: number, body: FieldReportOwnerCreateRequest, signal?: AbortSignal) {
    const form = new FormData();
    body.workTypeCodes.forEach((code) => form.append('workTypeCodes', code));
    body.issueTypeCodes?.forEach((code) => form.append('issueTypeCodes', code));
    if (body.workCmplYn) form.append('workCmplYn', body.workCmplYn);
    form.append('reportContent', body.reportContent);
    if (body.taskId != null) form.append('taskId', String(body.taskId));
    if (body.customerRequests?.length) form.append('customerRequests', JSON.stringify(body.customerRequests));
    if (body.inspectYn) form.append('inspectYn', body.inspectYn);
    if (body.photos?.length) {
      form.append('photos', JSON.stringify(body.photos));
      body.photoFiles?.forEach((f) => form.append('photoFiles', f));
    }
    return apiClient.post<ApiResponse<FieldReportOwnerCreateResult>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}/field-reports`,
      form,
      { signal },
    );
  },

  /** 작업보고 검수완료 — approvalYn='Y' (기획 004 D). 갱신된 상세 반환, 이미 처리된 건은 멱등 성공, 조건 위반 400. */
  inspectFieldReport(fieldReportId: number, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<FieldReportDetail>>(
      `${API_PRX.WORKLOG}/field-reports/${fieldReportId}/inspect`,
      undefined,
      { signal },
    );
  },

  /** 이슈 보고 조치 완료 — resolvedYn='Y' (기획 004 D). 갱신된 상세 반환, 이미 처리된 건은 멱등 성공, 조건 위반 400. */
  resolveFieldReport(fieldReportId: number, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<FieldReportDetail>>(
      `${API_PRX.WORKLOG}/field-reports/${fieldReportId}/resolve`,
      undefined,
      { signal },
    );
  },

  /** 작업요청 목록 조회 (API명세 §2.1) */
  getWorkOrders(query: WorkOrderListQuery, signal?: AbortSignal) {
    const params: Record<string, string | number> = {};
    if (query.sentDateFrom) params.sentDateFrom = query.sentDateFrom;
    if (query.sentDateTo) params.sentDateTo = query.sentDateTo;
    if (query.partnerId?.length) params.partnerId = query.partnerId.join(',');
    if (query.page != null) params.page = query.page;   // BE offset 페이징(page/size)
    if (query.size != null) params.size = query.size;
    if (query.sort) params.sort = query.sort;
    return apiClient
      .get<ApiResponse<PagedList<WorkOrderListItem>>>(`${API_PRX.WORKLOG}/work-orders`, { params, signal })
      .then((res) => {
        res.data.data?.items?.forEach(orderPhotos);
        return res;
      });
  },

  /** 공사현황(현장) 목록 조회 — 모드 C 필터 (API명세 §1.1) */
  getWorklogs(query: WorklogListQuery, signal?: AbortSignal) {
    const params: Record<string, string | number> = {};
    if (query.status) params.status = query.status;
    if (query.keyword) params.keyword = query.keyword;
    if (query.constStartDateFrom) params.constStartDateFrom = query.constStartDateFrom;
    if (query.constStartDateTo) params.constStartDateTo = query.constStartDateTo;
    if (query.worklogId?.length) params.worklogId = query.worklogId.join(',');
    if (query.siteId?.length) params.siteId = query.siteId.join(',');
    if (query.memberIds?.length) params.memberIds = query.memberIds.join(',');
    if (query.page != null) params.page = query.page;
    if (query.size != null) params.size = query.size;
    if (query.sort) params.sort = query.sort;
    return apiClient.get<ApiResponse<PagedList<WorklogListItem>>>(
      `${API_PRX.WORKLOG}/worklogs`,
      { params, signal },
    );
  },

  /** 공사현황 등록 — 글로벌 현장(siteId) 기준 신규 worklog 생성 (API명세 §1.2) */
  createWorklog(body: WorklogCreateRequest, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<WorklogCreateResult>>(
      `${API_PRX.WORKLOG}/worklogs`,
      body,
      { signal },
    );
  },

  /**
   * 작업요청 작성 현장 셀렉박스에서 site만 있는(worklogId=null) 현장을 골랐을 때 조회 (API명세 §1.2-1).
   * constStartDate/constEndDate가 둘 다 있으면 팝업 없이 그대로 공사현황을 자동 생성하고,
   * 하나라도 없으면 공사기간 입력 팝업(Step D)을 띄운다.
   */
  getScheduleLookup(siteId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<ScheduleLookupResult>>(
      `${API_PRX.WORKLOG}/worklogs/schedule-lookup`,
      { params: { siteId }, signal },
    );
  },

  /**
   * 공사현황 진행상태 변경 — PATCH /worklogs/{id} (API명세 §1.4). 시공완료(DONE) 처리 등.
   *
   * `syncSchedule=true` 를 보내면 BE가 **연동된 인디플래너 공사스케줄까지 함께 종료 처리**한다.
   * 이 값을 빼면 공사현황만 완료되고 스케줄은 진행 중으로 남아 두 서비스가 어긋난다.
   * (§1.4 명세에 아직 미기재 — BE 협의로 추가된 파라미터라 명세 개정 필요)
   */
  updateWorklogStatus(
    worklogId: number,
    progressStatus: string,
    opts?: { syncSchedule?: boolean },
    signal?: AbortSignal,
  ) {
    return apiClient.patch<ApiResponse<{ worklogId: number; progressStatus: string }>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}`,
      { progressStatus, ...(opts?.syncSchedule != null && { syncSchedule: opts.syncSchedule }) },
      { signal },
    );
  },

  /**
   * 공사기간(시작일·종료일) 변경 — PATCH /worklogs/{id} (API명세_20260721 §1.4).
   * 기간 변경 시 BE가 고객리포트 유효기간(validEndDate)을 재계산한다(파라미터 불필요).
   */
  updateWorklogPeriod(
    worklogId: number,
    period: { constStartDate: string; constEndDate: string },
    signal?: AbortSignal,
  ) {
    return apiClient.patch<ApiResponse<{ worklogId: number; siteId: number; updated: boolean }>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}`,
      period,
      { signal },
    );
  },

  /** 공사현황 삭제 — DELETE /worklogs/{id} (소프트 삭제, API명세 §1.5). */
  deleteWorklog(worklogId: number, signal?: AbortSignal) {
    return apiClient.delete<ApiResponse<void>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}`,
      { signal },
    );
  },

  /** 공사현황 삭제 전 확인 — GET /worklogs/{id}/delete-info. 삭제 확인창에 하위 데이터 건수를 안내하기 위함. */
  getWorklogDeleteInfo(worklogId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<WorklogDeleteInfo>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}/delete-info`,
      { signal },
    );
  },

  /**
   * 현장(siteId)에 등록된 스케줄의 공사기간 조회 — sv-schedule GET /schedule/schedules?siteId=.
   * 현장 등록 흐름에서 <b>기존 현장을 선택</b>했을 때 공사기간 입력 기본값을 채우는 데 쓴다.
   * 스케줄이 없거나 조회 실패면 null(호출부가 기본값 없이 진행).
   */
  /**
   * 현장(siteId)에 <b>아직 완료되지 않은</b> 공사현황이 이미 있는지 조회 (API명세 §1.1 목록 재사용).
   * 같은 현장에 공사현황을 중복 등록하면 서버가 거부하므로, 등록 흐름 앞단에서 먼저 확인한다.
   * 완료(DONE)는 재등록 대상이라 제외하고, 준비중(READY)·시공중(IN_PROGRESS)·지연(DELAYED)만 걸러낸다.
   * 조회 실패는 null로 degrade — 중복이면 어차피 등록 시점에 서버가 막는다.
   */
  getActiveWorklogBySite(siteId: number, signal?: AbortSignal): Promise<WorklogListItem | null> {
    return this.getWorklogs({ siteId: [siteId], size: 20 }, signal)
      .then((res) => {
        const items = res.data.data?.items ?? [];
        return (
          items.find((w) => w.siteId === siteId && w.progressStatus !== 'DONE') ?? null
        );
      })
      .catch(() => null);
  },

  getSiteSchedulePeriod(
    siteId: number,
    compId?: number | null,
    signal?: AbortSignal,
  ): Promise<{ startDate: string; endDate: string } | null> {
    const headers = compId != null ? { 'X-Comp-Id': String(compId) } : undefined;
    return apiClient
      .get<ApiResponse<{ schedules: { siteId: number; startDate: string | null; endDate: string | null }[] }>>(
        '/schedule/schedules',
        // ScheduleController#list — siteIds 필터. 이 값이 없으면 회사 전체 목록이 내려온다.
        { params: { siteIds: siteId }, headers, signal },
      )
      .then((res) => {
        const found = (res.data.data?.schedules ?? []).find(
          (s) => s.siteId === siteId && s.startDate && s.endDate,
        );
        return found ? { startDate: found.startDate!, endDate: found.endDate! } : null;
      })
      .catch(() => null); // 스케줄 미연동·조회 실패는 기본값 없음으로 degrade(등록 흐름을 막지 않는다)
  },

  /**
   * 반장(수신자) 목록 조회 (API명세 §2.10) — sv-worklog 경유(§서비스 간 호출 원칙, FE가 sv-member를 직접 호출하지 않는다).
   * 일괄 작업요청 "시공파트너 지정" 팝업(§5.4.2)의 검색에도 그대로 쓴다.
   */
  getPartners(keyword?: string, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<Partner[]>>(
      `${API_PRX.WORKLOG}/work-orders/partners`,
      { params: keyword ? { keyword } : undefined, signal },
    );
  },

  /** 시공반장(파트너) 신규 등록 — sv-worklog 경유(member-api §15 프록시). "+새 시공파트너 등록"(§5.4.2)에서 사용. */
  createPartner(body: PartnerCreateRequest, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<number>>(
      `${API_PRX.WORKLOG}/work-orders/partners`,
      body,
      { signal },
    );
  },

  /**
   * 작업요청 작성 — 현장 선택 기본정보(날씨+스케줄) 조회 (API명세 §2.12).
   * siteLat/siteLng 미전달 시 BE가 sv-customer 위경도로 대체(날씨 격자 변환).
   * workDate(yyyyMMdd) 전달 시에만 BE가 workTypes[]를 그 날짜 이후 공정(종료일 ≥ workDate — 진행 중 + 착공 전)으로 좁힌다(§6.6, opt-in —
   * 안 보내면 스케줄 전체 그대로 옴. 공정 스텝 차트·공종 후보 목록 등 다른 화면은 이 파라미터를 안 쓴다).
   */
  getWorkOrderSiteInfo(
    params: {
      siteId: number;
      siteAddress?: string | null;
      siteLat?: number | null;
      siteLng?: number | null;
      workDate?: string;
      /** 다른 화면에서 이 공정을 지정해 연 경우(REQ-11-03) — workDate 필터와 무관하게 이 공정을 목록에 포함시킨다 */
      taskId?: number;
    },
    signal?: AbortSignal,
  ) {
    const q: Record<string, string | number> = { siteId: params.siteId };
    if (params.siteAddress) q.siteAddress = params.siteAddress;
    if (params.siteLat != null) q.siteLat = params.siteLat;
    if (params.siteLng != null) q.siteLng = params.siteLng;
    if (params.workDate) q.workDate = params.workDate;
    if (params.taskId != null) q.taskId = params.taskId;
    return apiClient.get<ApiResponse<WorkOrderSiteInfo>>(
      `${API_PRX.WORKLOG}/work-orders/site-info`,
      { params: q, signal },
    );
  },

  /**
   * 작업요청 등록 + 발송 (API명세 §4.1.2) — multipart/form-data.
   * recipients는 JSON 문자열 폼 필드로 전송. axios가 FormData면 boundary 자동 설정.
   */
  createWorkOrder(worklogId: number, body: WorkOrderCreateRequest, signal?: AbortSignal) {
    const form = new FormData();
    form.append('requestKey', body.requestKey);
    form.append('workTypeCode', body.workTypeCode);
    if (body.taskId != null) form.append('taskId', String(body.taskId));
    form.append('orderContent', body.orderContent);
    if (body.customerRequests?.length) form.append('customerRequests', JSON.stringify(body.customerRequests));
    form.append('recipients', JSON.stringify(body.recipients));
    if (body.compName) form.append('compName', body.compName);
    // SIF-1 — '자재파트너가 시공' 공종이면 true. 안 보내면 BE가 공종 플래그로 자체 판정한다.
    if (body.materialConstruction != null) form.append('materialConstruction', String(body.materialConstruction));
    // 사진 메타는 JSON 문자열 폼필드, 바이너리는 photoFiles[] — photoSeq 순서로 대응(BE §2.3 규약).
    if (body.photos?.length) {
      form.append('photos', JSON.stringify(body.photos));
      body.photoFiles?.forEach((f) => form.append('photoFiles', f));
    }
    return apiClient.post<ApiResponse<WorkOrderCreateResult>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}/work-orders`,
      form,
      { signal },
    );
  },

  /** 내키워드 목록 조회 (API명세 §2.7) */
  getMyKeywords(signal?: AbortSignal) {
    return apiClient.get<ApiResponse<{ items: MyKeyword[] }>>(
      `${API_PRX.WORKLOG}/my-keywords`,
      { signal },
    );
  },

  /** 내키워드 등록 — 목록 맨 뒤에 추가 (API명세 §2.8, body: worklogKeyword) */
  addMyKeyword(worklogKeyword: string, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<{ keywordId: number; keywordSeq: number }>>(
      `${API_PRX.WORKLOG}/my-keywords`,
      { worklogKeyword },
      { signal },
    );
  },

  /** 내키워드 순번 일괄 재부여 — 최종 순서 전체 전달 (API명세 §2.9) */
  reorderMyKeywords(keywordIds: number[], signal?: AbortSignal) {
    return apiClient.put<ApiResponse<{ items: MyKeyword[] }>>(
      `${API_PRX.WORKLOG}/my-keywords/order`,
      { keywordIds },
      { signal },
    );
  },

  /** 내키워드 삭제 (API명세 §2.10) */
  deleteMyKeyword(keywordId: number, signal?: AbortSignal) {
    return apiClient.delete<ApiResponse<{ keywordId: number; deleted: boolean }>>(
      `${API_PRX.WORKLOG}/my-keywords/${keywordId}`,
      { signal },
    );
  },

  /** 일괄 작업요청 — 대상 공정 후보 조회 (API명세 §4.1.4). datePreset='ALL'은 현장 공사기간 전체(날짜 필터 없음). */
  getBulkCandidates(siteIds: number[], datePreset: BulkDatePreset, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<BulkCandidatesResp>>(
      `${API_PRX.WORKLOG}/work-orders/bulk-candidates`,
      { params: { siteIds: siteIds.join(','), datePreset }, signal },
    );
  },

  /**
   * 일괄 작업요청 — 배치 발송 (API명세 §4.1.3) — multipart/form-data.
   * items·orderContent·compName·photos는 payload JSON 폼 필드 하나로 묶어 전송(BE 계약).
   */
  createWorkOrdersBatch(body: BulkWorkOrderCreateRequest, signal?: AbortSignal) {
    const form = new FormData();
    form.append('payload', JSON.stringify({
      requestKey: body.requestKey,
      items: body.items,
      orderContent: body.orderContent,
      compName: body.compName,
      photos: body.photos,
    }));
    body.photoFiles?.forEach((f) => form.append('photoFiles', f));
    return apiClient.post<ApiResponse<BulkWorkOrderCreateResult>>(
      `${API_PRX.WORKLOG}/work-orders/batch`,
      form,
      { signal },
    );
  },

  /**
   * 일괄 작업요청 [공종별 미리보기] (신규 POST /work-orders/bulk-preview, 941 S16-8) — 선택한 공종마다 실제로 나갈 알림톡 본문. 조회 전용(발송 · 고객 요청 전환 없음).
   * 응답 items[] 는 요청과 같은 순서, 본문이 null 이면 그 공종은 미리볼 내용이 없다.
   */
  previewBulkWorkOrders(body: BulkPreviewRequest, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<BulkPreviewResult>>(
      `${API_PRX.WORKLOG}/work-orders/bulk-preview`,
      body,
      { signal },
    );
  },

  /** 공유이력 조회 (API명세 §4.1.14, 진입점 7) — [조회] 버튼 트리거(결정 7-7). */
  getShareHistory(query: ShareHistoryQuery, signal?: AbortSignal) {
    const params: Record<string, string | number | boolean> = {};
    if (query.siteId != null) params.siteId = query.siteId;
    if (query.type) params.type = query.type;
    if (query.viewStatus) params.viewStatus = query.viewStatus;
    if (query.keyword) params.keyword = query.keyword;
    if (query.dateFrom) params.dateFrom = query.dateFrom;
    if (query.dateTo) params.dateTo = query.dateTo;
    if (query.resendOnly) params.resendOnly = true;
    if (query.page != null) params.page = query.page;
    if (query.size != null) params.size = query.size;
    if (query.sort) params.sort = query.sort;
    return apiClient.get<ApiResponse<PagedList<ShareHistoryRow>>>(
      `${API_PRX.WORKLOG}/share-history`,
      { params, signal },
    );
  },

  /** 공유이력 재발송 (API명세 §4.1.15) — 같은 내용을 같은 수신자에게 다시 발송. */
  resendShare(sendHistId: number) {
    return apiClient.post<ApiResponse<ResendResult>>(`${API_PRX.WORKLOG}/share-history/${sendHistId}/resend`);
  },

  /** 공유취소(회수) — 발송자가 공유를 회수한다. 멱등. 고객 리포트 행에만 쓴다. */
  revokeShare(sendHistId: number) {
    return apiClient.post<ApiResponse<ShareRevokeResult>>(`${API_PRX.WORKLOG}/share-history/${sendHistId}/revoke`);
  },

  /** 공유취소 영향 조회 — 정보 제공만(취소를 막지 않는다). */
  getShareRevokeImpact(sendHistId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<ShareRevokeImpact>>(
      `${API_PRX.WORKLOG}/share-history/${sendHistId}/revoke-impact`,
      { signal },
    );
  },

  /** 발송자 미리보기 — 발송 당시 스냅샷(보낸 그대로). 고객 리포트 행은 CustomerReportPublic. 열람 기록이 남지 않는다. */
  getShareHistoryPreview(sendHistId: number, signal?: AbortSignal) {
    return apiClient
      .get<ApiResponse<CustomerReportPublic>>(`${API_PRX.WORKLOG}/share-history/${sendHistId}/preview`, { signal })
      .then((res) => {
        res.data.data?.reportCards?.forEach(orderPhotos);
        return res;
      });
  },

  /** 고객 리포트 게시(결정 52) — 새 소식을 게시한다. 게시할 새 소식이 없으면 published=false 로 200. */
  publishCustomerReport(worklogId: number, signal?: AbortSignal) {
    return apiClient.post<ApiResponse<CustomerReportPublishResult>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}/customer-reports/publish`,
      undefined,
      { signal },
    );
  },

  /** 헤더 배지용 새 게시 건수 — 지금 게시하면 새로 게시될 건수. */
  getCustomerReportPublishPending(worklogId: number, signal?: AbortSignal) {
    return apiClient.get<ApiResponse<{ newPostCount: number }>>(
      `${API_PRX.WORKLOG}/worklogs/${worklogId}/customer-reports/publish-pending`,
      { signal },
    );
  },
};
